import {
  type ApiOutputs,
  REFERRAL_EXAMPLE_MAX_BYTES,
  TEMPLATE_EXAMPLES_MAX,
  TEMPLATE_EXAMPLES_MAX_BYTES,
  type TemplateExample,
} from "@rosc/contracts";
import { referenceClient } from "@/lib/orpc/client";
import { sectionKey, type TemplateDocument } from "@/lib/template-document";
import { getVisitScope } from "@/lib/visit-queries";

// Note templates from previous notes (clinical lead, 29/10/2026): the
// referral letter "from an example letter" flow for note templates.
export type TemplateExtraction = ApiOutputs["createTemplateExtraction"];

/** Why these files cannot be sent, or null (checked before reading them). */
export const notesFilesProblem = (
  files: readonly File[],
): "tooMany" | "tooLarge" | null => {
  if (files.length > TEMPLATE_EXAMPLES_MAX) return "tooMany";
  if (
    files.some((file) => file.size > REFERRAL_EXAMPLE_MAX_BYTES) ||
    files.reduce((total, file) => total + file.size, 0) >
      TEMPLATE_EXAMPLES_MAX_BYTES
  )
    return "tooLarge";
  return null;
};

export const extractNoteTemplate = (
  examples: TemplateExample[],
  signal?: AbortSignal,
): Promise<TemplateExtraction> =>
  referenceClient.createTemplateExtraction(
    { query: {}, body: { examples } },
    { context: { expectedUserUid: getVisitScope()?.uid }, signal },
  );

/**
 * The editor's document with the draft's sections, instructions, style and
 * length. A name the clinician already typed is kept.
 */
export const applyTemplateDraft = (
  doc: TemplateDocument,
  draft: TemplateExtraction["draft"],
): TemplateDocument => ({
  ...doc,
  name: doc.name.trim() ? doc.name : draft.name,
  general: draft.instructions,
  sections: draft.sections.map((section) => ({
    key: sectionKey(),
    heading: section.heading,
    instructions: section.instructions,
    optional: section.optional,
  })),
  style: draft.style,
  length: draft.length,
});
